import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { BlueprintRole } from '@mn/shared';
import { useAuth } from './auth-context';

const STORAGE_KEY = 'blueprint-active-role';

export const ROLE_LABELS: Record<BlueprintRole, string> = {
  BLUEPRINT_ADMIN: 'Admin',
  PROJECT_MANAGER: 'Project Manager',
  TRADES: 'Trades',
  ELDER: 'Elder',
  PASTOR: 'Pastor',
  MINISTRY_LEADER: 'Ministry Leader',
  CONGREGATION_MEMBER: 'Congregant',
};

export const ROLE_COLORS: Record<BlueprintRole, string> = {
  BLUEPRINT_ADMIN: 'bg-red-100 text-red-700',
  PROJECT_MANAGER: 'bg-indigo-100 text-indigo-700',
  TRADES: 'bg-amber-100 text-amber-700',
  ELDER: 'bg-gray-100 text-gray-700',
  PASTOR: 'bg-blue-100 text-blue-700',
  MINISTRY_LEADER: 'bg-emerald-100 text-emerald-700',
  CONGREGATION_MEMBER: 'bg-purple-100 text-purple-700',
};

interface RoleContextValue {
  activeRole: BlueprintRole;
  switchRole: (role: BlueprintRole) => void;
  hasRole: (role: BlueprintRole) => boolean;
  hasAnyRole: (roles: BlueprintRole[]) => boolean;
  isAdmin: boolean;
}

const RoleContext = createContext<RoleContextValue | null>(null);

export function RoleProvider({ children }: { children: ReactNode }) {
  const { blueprintRoles, highestBlueprintRole } = useAuth();

  // Seed from localStorage; null until /me resolves.
  const [activeRole, setActiveRole] = useState<BlueprintRole | null>(
    () => localStorage.getItem(STORAGE_KEY) as BlueprintRole | null,
  );

  // Reconcile once roles are known: keep a valid current/stored choice, else default to the
  // deterministic highest role (blueprintRoles has no guaranteed order from the API).
  useEffect(() => {
    if (blueprintRoles.length === 0) return;
    setActiveRole((current) => {
      if (current && blueprintRoles.includes(current)) return current;
      return highestBlueprintRole ?? blueprintRoles[0];
    });
  }, [blueprintRoles, highestBlueprintRole]);

  const switchRole = useCallback((role: BlueprintRole) => {
    setActiveRole(role);
    localStorage.setItem(STORAGE_KEY, role);
  }, []);

  const hasRole = useCallback(
    (role: BlueprintRole) => blueprintRoles.includes(role),
    [blueprintRoles],
  );

  const hasAnyRole = useCallback(
    (roles: BlueprintRole[]) => roles.some((r) => blueprintRoles.includes(r)),
    [blueprintRoles],
  );

  // Always hand consumers a valid, non-null role. Validates against the loaded roles so a
  // stale stored value (a role since revoked) never leaks through.
  const resolvedRole: BlueprintRole =
    activeRole && blueprintRoles.includes(activeRole)
      ? activeRole
      : highestBlueprintRole ?? blueprintRoles[0] ?? 'CONGREGATION_MEMBER';

  const isAdmin = resolvedRole === 'BLUEPRINT_ADMIN';

  return (
    <RoleContext.Provider value={{ activeRole: resolvedRole, switchRole, hasRole, hasAnyRole, isAdmin }}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRoleContext(): RoleContextValue {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRoleContext must be used within RoleProvider');
  return ctx;
}
